import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plus, Trash2, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import WeekGrid, { weekDates, isoDate } from "@/components/WeekGrid";

export default function CoachCalendar() {
  const [events, setEvents] = useState([]);
  const [learners, setLearners] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [view, setView] = useState("upcoming");
  const [month, setMonth] = useState(new Date());
  const [weekAnchor, setWeekAnchor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ learner_id: "", session_id: null, title: "", date: "", start_time: "10:00", end_time: "11:00", notes: "" });

  const load = () => api.get("/coach/calendar").then(({ data }) => setEvents(data));
  useEffect(() => { load(); api.get("/coach/learners").then(({ data }) => setLearners(data)); }, []);
  useEffect(() => {
    if (form.learner_id) api.get(`/coach/learners/${form.learner_id}/sessions`).then(({ data }) => setSessions(data));
    else setSessions([]);
  }, [form.learner_id]);

  const openSlot = (date, time) => {
    const [h, m] = time.split(":").map(Number);
    const endH = String(h + 1).padStart(2, "0");
    setForm({ learner_id: "", session_id: null, title: "", date, start_time: time, end_time: `${endH}:${String(m).padStart(2, "0")}`, notes: "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, session_id: form.session_id || null };
      if (payload.session_id) {
        const s = sessions.find(x => x.id === payload.session_id);
        if (s) payload.session_number = s.number;
      }
      await api.post("/coach/calendar", payload);
      toast.success("Cours planifié");
      setOpen(false);
      setForm({ learner_id: "", session_id: null, title: "", date: "", start_time: "10:00", end_time: "11:00", notes: "" });
      load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce cours ?")) return;
    await api.delete(`/coach/calendar/${id}`); load();
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = [...events].filter(e => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const eventsByDate = events.reduce((acc, e) => { (acc[e.date] = acc[e.date] || []).push(e); return acc; }, {});
  const modifiers = { hasEvent: Object.keys(eventsByDate).map(d => new Date(d)) };

  const days = weekDates(weekAnchor);
  const weekLabel = `${days[0].toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif-accent text-3xl sm:text-4xl">Calendrier</h1>
          <p className="text-[#5C6561] mt-1">Tous les cours à venir.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-event-button" className="dec-pill bg-[#D95D39] hover:bg-[#C04E2D] text-white">
              <Plus className="w-4 h-4 mr-1" /> Planifier un cours
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader><DialogTitle>Planifier un cours</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Apprenant</Label>
                <Select value={form.learner_id} onValueChange={(v) => setForm({ ...form, learner_id: v, session_id: null })}>
                  <SelectTrigger><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{learners.map(l => <SelectItem key={l.id} value={l.id}>{l.first_name} {l.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Séance</Label>
                <Select value={form.session_id || "none"} onValueChange={(v) => setForm({ ...form, session_id: v === "none" ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="Aucune" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {sessions.map(s => <SelectItem key={s.id} value={s.id}>Séance {s.number} — {s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Titre</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Date</Label><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div><Label>Début</Label><Input type="time" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                <div><Label>Fin</Label><Input type="time" required value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <DialogFooter><Button type="submit" data-testid="event-submit" className="bg-[#D95D39] hover:bg-[#C04E2D] text-white">Créer</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="bg-[#F4EFEA]">
          <TabsTrigger value="upcoming" data-testid="view-list">Liste</TabsTrigger>
          <TabsTrigger value="week" data-testid="view-week">Semaine</TabsTrigger>
          <TabsTrigger value="month" data-testid="view-month">Mois</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "upcoming" && (
        <div className="mt-4 space-y-2">
          {upcoming.length === 0 && <div className="dec-card p-8 text-center text-[#5C6561]">Aucun cours à venir.</div>}
          {upcoming.map((e) => {
            const learner = learners.find(l => l.id === e.learner_id);
            return (
              <div key={e.id} className="dec-card p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#FDF0EC] text-[#D95D39] flex items-center justify-center"><CalendarDays className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{e.title}</div>
                  <div className="text-xs text-[#5C6561]">
                    {learner && `${learner.first_name} ${learner.last_name} • `}
                    {e.date} • {e.start_time} → {e.end_time}
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(e.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            );
          })}
        </div>
      )}

      {view === "week" && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <Button variant="outline" size="sm" className="bg-white" onClick={() => { const d = new Date(weekAnchor); d.setDate(d.getDate() - 7); setWeekAnchor(d); }} data-testid="week-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="font-semibold text-[#1F2421]">{weekLabel}</div>
            <Button variant="outline" size="sm" className="bg-white" onClick={() => { const d = new Date(weekAnchor); d.setDate(d.getDate() + 7); setWeekAnchor(d); }} data-testid="week-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <WeekGrid anchor={weekAnchor} events={events} onSlotClick={openSlot} />
        </div>
      )}

      {view === "month" && (
        <div className="mt-4 dec-card p-4 flex flex-col lg:flex-row gap-4">
          <CalendarPicker mode="single" month={month} onMonthChange={setMonth} modifiers={modifiers}
            modifiersClassNames={{ hasEvent: "bg-[#FDF0EC] text-[#D95D39] font-bold rounded-md" }} />
          <div className="flex-1">
            <h3 className="font-serif-accent text-lg mb-2">Événements du mois</h3>
            <div className="space-y-2 max-h-96 overflow-auto">
              {events.filter(e => {
                const d = new Date(e.date);
                return d.getMonth() === month.getMonth() && d.getFullYear() === month.getFullYear();
              }).map(e => {
                const learner = learners.find(l => l.id === e.learner_id);
                return (
                  <div key={e.id} className="p-3 rounded-lg bg-[#F4EFEA] text-sm">
                    <div className="font-semibold">{e.date} • {e.title}</div>
                    <div className="text-xs text-[#5C6561]">{learner && `${learner.first_name} ${learner.last_name}`} • {e.start_time}–{e.end_time}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
